import React, {
  Fragment,
  MouseEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  flexRender,
  Row,
  Cell,
} from '@tanstack/react-table';
import isFunction from 'lodash/isFunction';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  getTableColumnProps,
  TABLE_ROW_COMMON_CLASSES,
} from 'core/components/VirtualTable/helpers';
import Skeleton from 'core/components/Skeleton';
import noop from 'lodash/noop';

type Props<T> = {
  row: Row<T>;
  onClick?: (row: Row<T>) => void;
  className?: string;
  cellClassName?: ((row: Row<T>, cell: Cell<T, unknown>) => string) | string;
  subRowClassName?: ((row: Row<T>) => string) | string;
  hasSkeleton?: boolean;
};

type CellProps<T> = Cell<T, unknown> & {
  className?: string;
  hasSkeleton?: boolean;
};

function CellRenderer<T>({
  column,
  getContext,
  className,
  hasSkeleton,
}: CellProps<T>) {
  const columnDef = (column.columnDef as ColumnDef<T>);
  const skeleton = columnDef?.skeleton ?? <Skeleton />;
  return (
    <div
      {...getTableColumnProps(column.columnDef, className, hasSkeleton)}
      role="button"
      tabIndex={-1}
      onClick={(e: MouseEvent<HTMLElement>) => {
        if (isFunction(columnDef?.onClick) && !hasSkeleton) {
          columnDef.onClick(e, getContext().row, columnDef);
        }
      }}
      onKeyDown={noop}
    >
      {flexRender(
        hasSkeleton ? skeleton : column.columnDef.cell,
        getContext(),
      )}
    </div>
  );
}

function useAnimation(
  expandable: boolean,
  isExpanded: boolean,
) {
  const [isTransitioning, setIsTransitioning] = useState(false);

  useLayoutEffect(() => {
    setIsTransitioning(true);
  }, [isExpanded, expandable]);

  return {
    setIsTransitioning,
    isTransitioning,
  };
}

export default function TableRow<T>({
  row,
  className,
  cellClassName,
  subRowClassName,
  onClick,
  hasSkeleton,
}: Props<T>) {
  const isExpanded = row.getIsExpanded();
  const expandable = row.getCanExpand();
  const subRowsRef = useRef<HTMLDivElement>(null);

  const {
    isTransitioning,
    setIsTransitioning,
  } = useAnimation(expandable, isExpanded);

  return (
    <Fragment key={row.id}>
      <div
        role="button"
        tabIndex={-1}
        className={clsx(TABLE_ROW_COMMON_CLASSES, className)}
        onClick={() => {
          if (!hasSkeleton) {
            onClick?.(row);
          }
        }}
        onKeyDown={noop}
      >
        {row?.getVisibleCells()?.map((cell) => (
          <CellRenderer
            key={cell.id}
            {...cell}
            hasSkeleton={hasSkeleton}
            className={isFunction(cellClassName) ? cellClassName(row, cell) : cellClassName}
          />
        ))}
      </div>
      {expandable && (
        <div
          ref={subRowsRef}
          className="overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{ height: isExpanded ? `${subRowsRef.current?.scrollHeight}px` : '0px' }}
          onTransitionEnd={() => {
            setTimeout(() => {
              setIsTransitioning(false);
            }, 0);
          }}
        >
          {(isTransitioning || isExpanded) && (row.subRows ?? []).map((item) => (
            <TableRow<T>
              className={clsx(
                className,
                isFunction(subRowClassName) ? subRowClassName(item) : subRowClassName,
              )}
              cellClassName={cellClassName}
              onClick={onClick}
              hasSkeleton={hasSkeleton}
              key={item.id}
              row={item}
            />
          ))}
        </div>
      )}
    </Fragment>
  );
}
